import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Bulwark — defend-the-core, redesigned for 3D depth + engagement loop.
 * Game 03 of 12. (v2 — feel-pass sweep.)
 *
 * Scoring contract preserved: xmur3-seeded enemy schedule + kill claims
 * (idx, t) + duration are server-replayed unchanged. Visual layer rebuilt:
 * 3D core + spheres with trails + 3rd-person camera + biome cycle.
 */

const ARENA_RADIUS = 18;
const CORE_RADIUS = 1.4;
const ENEMY_RADIUS = 0.55;
const CHAMPION_RADIUS = 0.95;
const TANK_RADIUS = 1.05;
const ENEMY_MIN_SPEED = 1.6;
const ENEMY_MAX_SPEED = 3.4;
const TAP_HIT_RADIUS_BASE = 1.7;
const LEAK_LIMIT = 5;
const MAX_DURATION_S = 180;
const PB_KEY = "aether.bulwark_pb";
const CHAMPION_EVERY = 13;
const APPROACH_GATE = 5;

// v4 — archetype constants (mirror scoring-bulwark.ts).
const SPRINTER_EVERY = 11;
const SPLITTER_EVERY = 17;
const TANK_EVERY = 19;
const SPRINTER_SPEED_MULT = 1.5;
const TANK_SPEED_MULT = 0.7;
const TANK_HITS_REQUIRED = 3;
const ULTIMATE_KILL_COST = 25;

type EnemyKind = "drifter" | "sprinter" | "tank" | "splitter";
function enemyKindLocal(idx: number): EnemyKind {
  if (idx > 0 && idx % TANK_EVERY === 0) return "tank";
  if (idx > 0 && idx % SPLITTER_EVERY === 0) return "splitter";
  if (idx > 0 && idx % SPRINTER_EVERY === 0) return "sprinter";
  return "drifter";
}
function kindSpeedMultLocal(k: EnemyKind): number {
  if (k === "sprinter") return SPRINTER_SPEED_MULT;
  if (k === "tank") return TANK_SPEED_MULT;
  return 1;
}
const KIND_COLOR: Record<EnemyKind, { body: number; emissive: number }> = {
  drifter:  { body: 0xff5b3a, emissive: 0xff2010 },
  sprinter: { body: 0x66ff88, emissive: 0x22aa44 },
  tank:     { body: 0x6688ff, emissive: 0x3344cc },
  splitter: { body: 0xffaa44, emissive: 0xff6622 },
};

// v3 — meta-game constants (mirror scoring-bulwark.ts)
const SHOP_INTERVAL_S = 30;
const SHOP_DURATION_S = 5;
const SLOW_FIELD_RADIUS = 6;
const UPGRADE_COSTS: Record<UpgradeKind, number[]> = {
  range: [8, 16, 24],
  burst: [12, 24],
  slow_field: [10, 20],
};
const BURST_RADIUS = 3.5;

export type UpgradeKind = "range" | "burst" | "slow_field";
export interface UpgradePurchase { t: number; kind: UpgradeKind; level: number; }
export const UPGRADE_LABEL: Record<UpgradeKind, string> = {
  range: "Range",
  burst: "Burst",
  slow_field: "Slow Field",
};

interface BiomePalette { core: number; coreEmissive: number; floor: number; rim: number; enemy: number; enemyEmissive: number; }
const PALETTES: BiomePalette[] = [
  { core: 0x46ffe0, coreEmissive: 0x00aa90, floor: 0x05080f, rim: 0xff9248, enemy: 0xff5b3a, enemyEmissive: 0xff2010 },
  { core: 0x9a72ff, coreEmissive: 0x6040cc, floor: 0x0a0518, rim: 0xff3df0, enemy: 0xffd54a, enemyEmissive: 0xff8800 },
  { core: 0xff3df0, coreEmissive: 0xcc1aa0, floor: 0x100515, rim: 0x46ffe0, enemy: 0x88ccff, enemyEmissive: 0x4060ff },
];

function xmur3(s: string): () => number {
  let h = 1779033703 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

interface ScheduledEnemy { idx: number; spawn_t: number; arrival_t: number; angle: number; speed: number; champion: boolean; kind: EnemyKind; }

function buildSchedule(seed: number, duration: number): ScheduledEnemy[] {
  const rng = xmur3(`bulwark:${seed}`);
  const out: ScheduledEnemy[] = [];
  let t = 0;
  let idx = 0;
  while (t < duration && out.length < 4000) {
    const density = 0.6 + (t / MAX_DURATION_S) * 2.4;
    const u = (rng() % 1_000_000) / 1_000_000;
    const interval = -Math.log(Math.max(u, 1e-6)) / density;
    t += interval;
    if (t >= duration) break;
    const angle = ((rng() % 1_000_000) / 1_000_000) * Math.PI * 2;
    const baseSpeed = ENEMY_MIN_SPEED + ((rng() % 1_000_000) / 1_000_000) * (ENEMY_MAX_SPEED - ENEMY_MIN_SPEED);
    const kind = enemyKindLocal(idx);
    const speed = baseSpeed * kindSpeedMultLocal(kind);
    out.push({ idx, spawn_t: t, arrival_t: t + (ARENA_RADIUS - CORE_RADIUS) / speed, angle, speed, champion: idx > 0 && idx % CHAMPION_EVERY === 0, kind });
    idx++;
  }
  return out;
}

interface ActiveEnemy {
  sched: ScheduledEnemy;
  group: THREE.Group;
  body: THREE.Mesh;
  trail: THREE.Mesh;
  /** v4 — remaining hits before kill (3 for tank, 1 otherwise). */
  hitsLeft: number;
  /** v4 — recorded prior hit times for multi-hit enemies. */
  hitTimes: number[];
}

export interface BulwarkCallbacks {
  onHud: (state: {
    durationS: number;
    kills: number;
    leaks: number;
    energy: number;
    stacks: Record<UpgradeKind, number>;
    shopOpen: boolean;
    shopTimeLeft: number;
    /** v4 — kills since last ult, charges toward next ult. */
    ultimateCharge: number;
    ultimateReady: boolean;
  }) => void;
  onGameOver: (result: {
    durationS: number;
    score: number;
    kills: number;
    leaks: number;
    isNewBest: boolean;
    killClaims: { idx: number; t: number; hits_t?: number[] }[];
    upgrades: UpgradePurchase[];
    championKills: number;
    /** v4 — ultimate activations for server validation. */
    ultimates: { t: number }[];
    /** v5 — per-archetype kill breakdown (client mirror of server tally;
     *  same idx-modulo rule, so values match the validator's response). */
    tankKills: number;
    sprinterKills: number;
    splitterKills: number;
  }) => void;
}

export class Bulwark {
  private engine: Engine;
  private audio = new AudioBed();
  private schedule: ScheduledEnemy[];
  private active: ActiveEnemy[] = [];
  private nextSpawnIdx = 0;
  private killClaims: { idx: number; t: number; hits_t?: number[] }[] = [];
  private kills = 0;
  private leaks = 0;
  private championKills = 0;
  // v5 — per-archetype tallies surfaced on the run-end card.
  private tankKills = 0;
  private sprinterKills = 0;
  private splitterKills = 0;
  // v4 — ultimate state.
  private ultimateCharge = 0;
  private ultimates: { t: number }[] = [];
  private ultFx: THREE.Mesh | null = null;
  private ultFxOpacity = 0;
  // v5 — engine boost pulse on ult: drives the shared FOV/chroma/god-ray
  // stack via engine.setBoost(). Decays from 1 → 0 over BOOST_DECAY_S,
  // then the camera FOV is restored to Bulwark's tight value.
  private ultBoost = 0;
  private readonly BULWARK_FOV = 56;
  private onKeyDown = (e: KeyboardEvent) => this.handleKey(e);
  // v3 meta-game state
  private energy = 0;
  private stacks: Record<UpgradeKind, number> = { range: 0, burst: 0, slow_field: 0 };
  private upgrades: UpgradePurchase[] = [];
  private shopOpen = false;
  private shopEndsAt = 0;
  private nextShopAt = SHOP_INTERVAL_S;
  private killsSinceLastBurst = 0;
  private burstFx: THREE.Mesh | null = null;
  private burstFxOpacity = 0;
  private slowFieldHalo: THREE.Mesh | null = null;
  private startedAt = 0;
  private gameOver = false;
  private cb: BulwarkCallbacks;
  private canvas: HTMLCanvasElement;
  private core: THREE.Mesh;
  private coreMat: THREE.MeshStandardMaterial;
  private coreHalo: THREE.Mesh;
  private floor: THREE.Mesh;
  private floorMat: THREE.MeshStandardMaterial;
  private rim: THREE.Mesh;
  private rimMat: THREE.MeshBasicMaterial;
  private currentBiome = 0;
  private targetBiome = 0;
  private biomeBlend = 0;
  private flashFx: THREE.Mesh;
  private flashOpacity = 0;
  // v6 — three parallax starfield layers (far / mid / near) so the
  // arena reads as suspended in deep space, not as a 2D sprite on a
  // floor. Per memory feedback-sibling-games-shallow §"real 3D scene
  // with depth cues". Mirrors Phalanx 2bc3ebc; uses fog:false because
  // Bulwark's linear fog clips at distance 60 (stars at z=-180 would
  // otherwise be fully fogged).
  private starLayers: THREE.Points[] = [];
  private onPointerDown = (e: PointerEvent) => this.handleTap(e);

  constructor(canvas: HTMLCanvasElement, cb: BulwarkCallbacks, seed = Math.floor(Math.random() * 0x7fffffff)) {
    this.canvas = canvas;
    this.cb = cb;
    this.schedule = buildSchedule(seed, MAX_DURATION_S);
    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });

    // 3rd-person camera: behind-and-above the core, looking forward.
    this.engine.camera.position.set(0, -14, 18);
    this.engine.camera.lookAt(0, 3, 0);
    this.engine.camera.fov = this.BULWARK_FOV;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(0x06080f, 24, 60);

    const ambient = new THREE.AmbientLight(0x6080a0, 0.4);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 1.1);
    key.position.set(8, 6, 12);
    this.engine.scene.add(key);
    const fill = new THREE.DirectionalLight(0x4060ff, 0.5);
    fill.position.set(-8, 4, 8);
    this.engine.scene.add(fill);

    // Floor disc anchors depth.
    const floorGeo = new THREE.CircleGeometry(ARENA_RADIUS * 1.4, 80);
    this.floorMat = new THREE.MeshStandardMaterial({ color: PALETTES[0].floor, roughness: 0.9, metalness: 0.15 });
    this.floor = new THREE.Mesh(floorGeo, this.floorMat);
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.position.y = -1.2;
    this.engine.scene.add(this.floor);

    // Concentric arena rings on the floor (radar-style).
    for (const r of [6, 11, 16]) {
      const ringGeo = new THREE.RingGeometry(r - 0.04, r + 0.04, 96);
      const ringMat = new THREE.MeshBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.22, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -1.18;
      this.engine.scene.add(ring);
    }

    // Outer rim — perimeter glow.
    const rimGeo = new THREE.RingGeometry(ARENA_RADIUS - 0.08, ARENA_RADIUS + 0.08, 96);
    this.rimMat = new THREE.MeshBasicMaterial({ color: PALETTES[0].rim, transparent: true, opacity: 0.65, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.rim = new THREE.Mesh(rimGeo, this.rimMat);
    this.rim.rotation.x = -Math.PI / 2;
    this.rim.position.y = -1.16;
    this.engine.scene.add(this.rim);

    // Core — 3D sphere with emissive material + halo ring.
    const coreGeo = new THREE.SphereGeometry(CORE_RADIUS, 32, 24);
    this.coreMat = new THREE.MeshStandardMaterial({ color: PALETTES[0].core, emissive: PALETTES[0].coreEmissive, emissiveIntensity: 1.4, roughness: 0.25, metalness: 0.6 });
    this.core = new THREE.Mesh(coreGeo, this.coreMat);
    this.core.position.set(0, 0, 0);
    this.engine.scene.add(this.core);

    const haloGeo = new THREE.RingGeometry(CORE_RADIUS + 0.4, CORE_RADIUS + 1.1, 48);
    const haloMat = new THREE.MeshBasicMaterial({ color: PALETTES[0].core, transparent: true, opacity: 0.45, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.coreHalo = new THREE.Mesh(haloGeo, haloMat);
    this.coreHalo.rotation.x = -Math.PI / 2;
    this.coreHalo.position.y = -1.1;
    this.engine.scene.add(this.coreHalo);

    // Tap-flash effect — a brief expanding sphere where the player hits.
    const flashGeo = new THREE.SphereGeometry(1.5, 16, 12);
    const flashMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending });
    this.flashFx = new THREE.Mesh(flashGeo, flashMat);
    this.engine.scene.add(this.flashFx);

    // v6 — parallax starfield. Three THREE.Points layers at varying Z
    // depth give the arena genuine 3D distance; the camera's tilted
    // forward view (pos y=-14, looking at y=3) treats world -Z as
    // "deep ahead." Positions seeded off the run seed so the same
    // starscape recurs per-run. fog:false because Bulwark's linear
    // fog (far=60) would clip every layer otherwise. Y biased slightly
    // upward so most points sit above the y=0 arena plane (the "sky").
    const makeStars = (count: number, z: number, radius: number, opts: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      let s = (seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 1] = (r() + 0.05) * radius * 1.3;
        positions[i * 3 + 2] = z + (r() - 0.5) * 8;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: opts.color, size: opts.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(600, -180, 240, { size: 0.6, color: 0x8a92b8 });   // far — cool blue/gray
    makeStars(300,  -90, 140, { size: 0.5, color: 0xaab0d4 });   // mid — slightly brighter
    makeStars(120,  -45,  90, { size: 0.45, color: 0xffe8c8 });  // near — warm tint

    this.canvas.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("keydown", this.onKeyDown);
  }

  private handleKey(e: KeyboardEvent) {
    if (this.gameOver) return;
    if (e.target instanceof HTMLInputElement) return;
    // Q or Space-bar fires the ultimate when ready.
    if (e.code !== "KeyQ") return;
    if (this.ultimateCharge < ULTIMATE_KILL_COST) return;
    const t = performance.now() / 1000 - this.startedAt;
    this.fireUltimate(t);
  }

  private fireUltimate(t: number) {
    this.ultimateCharge -= ULTIMATE_KILL_COST;
    this.ultimates.push({ t: Math.max(0, t) });
    // Detonate: damage every active enemy. Multi-hit enemies (tanks)
    // take one stack of damage per ult; that means a tank caught fresh
    // becomes a 2-hit enemy. Tanks fully dead from the ult are credited.
    const survivors: ActiveEnemy[] = [];
    for (const en of this.active) {
      en.hitsLeft -= 1;
      en.hitTimes.push(Math.max(0, t));
      if (en.hitsLeft <= 0) {
        this.recordKill(en, t);
      } else {
        survivors.push(en);
      }
    }
    this.active = survivors;
    // Visual: full-screen white ring + brief screen flash.
    if (!this.ultFx) {
      const ringGeo = new THREE.RingGeometry(0.5, ARENA_RADIUS * 1.05, 96);
      const ringMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
      this.ultFx = new THREE.Mesh(ringGeo, ringMat);
      this.ultFx.rotation.x = -Math.PI / 2;
      this.ultFx.position.y = -1.0;
      this.engine.scene.add(this.ultFx);
    }
    this.ultFx.scale.setScalar(0.15);
    this.ultFxOpacity = 1;
    // v5 — kick the shared engine cinema stack (FOV widen + chromatic
    // aberration + god-ray streaks) for ~0.7s. The frame loop decays it
    // and restores Bulwark's tight FOV when done.
    this.ultBoost = 1;
    this.audio.chime(24, 0.5);
    this.audio.chime(31, 0.3);
  }

  /** v4 — book-keeping after an enemy is confirmed dead (any kill source). */
  private recordKill(target: ActiveEnemy, t: number) {
    this.kills++;
    if (target.sched.champion) this.championKills++;
    if (target.sched.kind === "tank") this.tankKills++;
    else if (target.sched.kind === "sprinter") this.sprinterKills++;
    else if (target.sched.kind === "splitter") this.splitterKills++;
    this.energy++;
    this.ultimateCharge++;
    const claim: { idx: number; t: number; hits_t?: number[] } = {
      idx: target.sched.idx,
      t: Math.max(0, t),
    };
    if (target.sched.kind === "tank" && target.hitTimes.length >= TANK_HITS_REQUIRED - 1) {
      claim.hits_t = target.hitTimes.slice(0, TANK_HITS_REQUIRED - 1);
    }
    this.killClaims.push(claim);
    this.engine.scene.remove(target.group);
    target.body.geometry.dispose();
    target.trail.geometry.dispose();
  }

  armAudio() { this.audio.arm(); }
  setVolume(v: number) { this.audio.setVolume(v); }
  setMuted(m: boolean) { this.audio.setMuted(m); }

  start() {
    this.startedAt = performance.now() / 1000;
    this.gameOver = false;
    this.kills = 0;
    this.leaks = 0;
    this.championKills = 0;
    this.tankKills = 0;
    this.sprinterKills = 0;
    this.splitterKills = 0;
    this.killClaims = [];
    this.nextSpawnIdx = 0;
    this.active = [];
    this.currentBiome = 0;
    this.targetBiome = 0;
    this.biomeBlend = 0;
    this.energy = 0;
    this.stacks = { range: 0, burst: 0, slow_field: 0 };
    this.upgrades = [];
    this.shopOpen = false;
    this.shopEndsAt = 0;
    this.nextShopAt = SHOP_INTERVAL_S;
    this.killsSinceLastBurst = 0;
    this.ultimateCharge = 0;
    this.ultimates = [];
    this.ultBoost = 0;
    // Make sure a re-started run resets to Bulwark's tight FOV (a prior
    // run could have left the engine mid-pulse).
    this.engine.camera.fov = this.BULWARK_FOV;
    this.engine.camera.updateProjectionMatrix();
    this.applyBiome(PALETTES[0]);
    // v6 — baseline drone; per-frame frame() ramps it adaptively up
    // to 0.7 based on max(ult charge ratio, leak danger ratio). Was
    // a fixed 0.4 before; the adaptive ramp is the §"escalation
    // hook" the shallow-sibling memory called for.
    this.audio.setDroneIntensity(0.15);
    this.engine.start();
  }

  stop() { this.engine.stop(); }

  dispose() {
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("keydown", this.onKeyDown);
    // v6 — release adaptive drone + parallax star layers so a
    // navigate-away → mount cycle doesn't bleed audio or leak
    // THREE.Points objects into the scene graph.
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    for (const e of this.active) {
      this.engine.scene.remove(e.group);
      e.body.geometry.dispose();
      e.trail.geometry.dispose();
    }
    this.engine.dispose();
  }

  private applyBiome(p: BiomePalette) {
    this.coreMat.color.setHex(p.core);
    this.coreMat.emissive.setHex(p.coreEmissive);
    this.floorMat.color.setHex(p.floor);
    this.rimMat.color.setHex(p.rim);
    (this.coreHalo.material as THREE.MeshBasicMaterial).color.setHex(p.core);
  }

  private blendBiomes(a: BiomePalette, b: BiomePalette, t: number) {
    this.coreMat.color.copy(new THREE.Color(a.core).lerp(new THREE.Color(b.core), t));
    this.coreMat.emissive.copy(new THREE.Color(a.coreEmissive).lerp(new THREE.Color(b.coreEmissive), t));
    this.floorMat.color.copy(new THREE.Color(a.floor).lerp(new THREE.Color(b.floor), t));
    this.rimMat.color.copy(new THREE.Color(a.rim).lerp(new THREE.Color(b.rim), t));
    (this.coreHalo.material as THREE.MeshBasicMaterial).color.copy(new THREE.Color(a.core).lerp(new THREE.Color(b.core), t));
  }

  private handleTap(e: PointerEvent) {
    if (this.gameOver) return;
    const t = performance.now() / 1000 - this.startedAt;
    const rect = this.canvas.getBoundingClientRect();
    const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    // Ray onto the y=0 plane (where enemies/core live).
    const ndc = new THREE.Vector3(ndcX, ndcY, 0.5).unproject(this.engine.camera);
    const dir = ndc.sub(this.engine.camera.position).normalize();
    const dist = -this.engine.camera.position.y / dir.y;
    const wx = this.engine.camera.position.x + dir.x * dist;
    const wz = this.engine.camera.position.z + dir.z * dist;
    // Enemies move in XZ plane (camera looks down at y=0).
    let best: ActiveEnemy | null = null;
    const baseTapRadius = TAP_HIT_RADIUS_BASE + this.stacks.range * 0.4;
    // Slow-field boost: enemies within the inner ring get a larger
    // effective tap radius (player can swat them easily). Each stack
    // adds +0.6u; +1.2u at L2.
    const slowFieldBoost = this.stacks.slow_field * 0.6;
    let bestDist = baseTapRadius + slowFieldBoost; // upper bound for filtering
    for (const en of this.active) {
      const d = Math.hypot(en.group.position.x - wx, en.group.position.z - wz);
      const enemyR = Math.hypot(en.group.position.x, en.group.position.z);
      const inField = this.stacks.slow_field > 0 && enemyR < SLOW_FIELD_RADIUS;
      const radius = inField ? baseTapRadius + slowFieldBoost : baseTapRadius;
      if (d < radius && d < bestDist) { bestDist = d; best = en; }
    }
    if (best) {
      this.registerHit(best, t);
    }
  }

  /** v4 — handles a single hit; multi-hit enemies survive until the last one. */
  private registerHit(target: ActiveEnemy, t: number) {
    target.hitsLeft -= 1;
    // Visual flash for every hit (so tanks read as "still alive, but hurt").
    this.flashFx.position.set(target.group.position.x, 0.5, target.group.position.z);
    this.flashFx.scale.setScalar(target.sched.champion ? 1.4 : 1);
    this.flashOpacity = 1;
    if (target.hitsLeft > 0) {
      // Record the hit time + brighten the body for danger feedback.
      target.hitTimes.push(Math.max(0, t));
      const bodyMat = target.body.material as THREE.MeshStandardMaterial;
      bodyMat.emissiveIntensity = 2.4;
      this.audio.chime(3, 0);
      return;
    }
    // Final hit — kill.
    this.recordKill(target, t);
    this.active = this.active.filter((a) => a !== target);
    this.audio.chime(target.sched.champion ? 12 : 4);
    // Burst upgrade — every Nth kill triggers a radial blast.
    if (this.stacks.burst > 0) {
      this.killsSinceLastBurst++;
      const burstEvery = this.stacks.burst >= 2 ? 4 : 8;
      if (this.killsSinceLastBurst >= burstEvery) {
        this.killsSinceLastBurst = 0;
        this.triggerBurst(target.group.position.x, target.group.position.z, t);
      }
    }
  }

  private triggerBurst(cx: number, cz: number, t: number) {
    this.audio.chime(19, 0.4);
    // Damage active enemies within BURST_RADIUS. Multi-hit enemies take
    // one stack of damage (so a tank caught in burst becomes weakened).
    const survivors: ActiveEnemy[] = [];
    for (const en of this.active) {
      const d = Math.hypot(en.group.position.x - cx, en.group.position.z - cz);
      if (d <= BURST_RADIUS) {
        en.hitsLeft -= 1;
        en.hitTimes.push(Math.max(0, t));
        if (en.hitsLeft <= 0) {
          this.recordKill(en, t);
        } else {
          survivors.push(en);
        }
      } else {
        survivors.push(en);
      }
    }
    this.active = survivors;
    // Spawn a brief expanding ring visual at the burst center.
    if (!this.burstFx) {
      const burstGeo = new THREE.RingGeometry(0.5, BURST_RADIUS, 48);
      const burstMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
      this.burstFx = new THREE.Mesh(burstGeo, burstMat);
      this.burstFx.rotation.x = -Math.PI / 2;
      this.engine.scene.add(this.burstFx);
    }
    this.burstFx.position.set(cx, 0.6, cz);
    this.burstFx.scale.setScalar(0.2);
    this.burstFxOpacity = 1.0;
  }

  /** Public — bootstrap calls this when the shop UI button is tapped. */
  purchaseUpgrade(kind: UpgradeKind): boolean {
    if (this.gameOver || !this.shopOpen) return false;
    const nextLevel = this.stacks[kind] + 1;
    const costs = UPGRADE_COSTS[kind];
    if (nextLevel > costs.length) return false;
    const cost = costs[nextLevel - 1];
    if (this.energy < cost) return false;
    this.energy -= cost;
    this.stacks[kind] = nextLevel;
    const t = performance.now() / 1000 - this.startedAt;
    this.upgrades.push({ t: Math.max(0, t), kind, level: nextLevel });
    this.audio.chime(12, -0.4);
    this.audio.chime(7, 0.4);
    return true;
  }

  private frame(dt: number, _t: number) {
    if (this.gameOver) return;
    const elapsed = performance.now() / 1000 - this.startedAt;

    // Biome cycle — every 25 spawned-enemies (not kills) rotates palette.
    const targetBiome = Math.floor(this.nextSpawnIdx / 25) % PALETTES.length;
    if (targetBiome !== this.targetBiome) {
      this.currentBiome = this.targetBiome;
      this.targetBiome = targetBiome;
      this.biomeBlend = 0;
    }
    if (this.currentBiome !== this.targetBiome) {
      this.biomeBlend = Math.min(1, this.biomeBlend + dt * 0.5);
      this.blendBiomes(PALETTES[this.currentBiome], PALETTES[this.targetBiome], this.biomeBlend);
      if (this.biomeBlend >= 1) this.currentBiome = this.targetBiome;
    }

    // Spawn schedule.
    while (this.nextSpawnIdx < this.schedule.length && this.schedule[this.nextSpawnIdx].spawn_t <= elapsed) {
      const s = this.schedule[this.nextSpawnIdx];
      const palette = PALETTES[this.currentBiome];
      // v4 — radius reflects archetype (tanks larger, sprinters slimmer).
      let r = ENEMY_RADIUS;
      if (s.kind === "tank") r = TANK_RADIUS;
      else if (s.kind === "splitter") r = ENEMY_RADIUS * 1.25;
      else if (s.kind === "sprinter") r = ENEMY_RADIUS * 0.85;
      if (s.champion) r = Math.max(r, CHAMPION_RADIUS);
      // Color by kind unless champion (champions stay gold).
      const kc = KIND_COLOR[s.kind];
      const bodyGeo = new THREE.SphereGeometry(r, 16, 12);
      const bodyMat = new THREE.MeshStandardMaterial({
        color: s.champion ? 0xffd54a : (s.kind === "drifter" ? palette.enemy : kc.body),
        emissive: s.champion ? 0xff8800 : (s.kind === "drifter" ? palette.enemyEmissive : kc.emissive),
        emissiveIntensity: s.champion ? 1.6 : 1.0,
        roughness: 0.35,
        metalness: 0.5,
      });
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      // Comet trail — sprinter trail is longer.
      const trailLen = s.kind === "sprinter" ? r * 6 : r * 4;
      const trailGeo = new THREE.ConeGeometry(r * 0.55, trailLen, 8, 1, true);
      const trailMat = new THREE.MeshBasicMaterial({
        color: s.champion ? 0xffd54a : (s.kind === "drifter" ? palette.enemy : kc.body),
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
      });
      const trail = new THREE.Mesh(trailGeo, trailMat);
      trail.position.y = 0;
      trail.position.x = Math.cos(s.angle) * r * 2;
      trail.position.z = Math.sin(s.angle) * r * 2;
      trail.lookAt(0, 0, 0);
      trail.rotateX(Math.PI / 2);
      const group = new THREE.Group();
      group.add(body);
      group.add(trail);
      group.position.set(Math.cos(s.angle) * ARENA_RADIUS, 0, Math.sin(s.angle) * ARENA_RADIUS);
      this.engine.scene.add(group);
      const hitsLeft = s.kind === "tank" ? TANK_HITS_REQUIRED : 1;
      this.active.push({ sched: s, group, body, trail, hitsLeft, hitTimes: [] });
      this.nextSpawnIdx++;
    }

    // Shop open/close every SHOP_INTERVAL_S.
    if (!this.shopOpen && elapsed >= this.nextShopAt && elapsed < MAX_DURATION_S) {
      this.shopOpen = true;
      this.shopEndsAt = elapsed + SHOP_DURATION_S;
      this.nextShopAt += SHOP_INTERVAL_S;
      this.audio.chime(7, -0.3);
      this.audio.chime(12, 0.3);
    }
    if (this.shopOpen && elapsed >= this.shopEndsAt) {
      this.shopOpen = false;
    }

    // Slow-field halo (visible when stacks.slow_field > 0).
    if (this.stacks.slow_field > 0 && !this.slowFieldHalo) {
      const ringGeo = new THREE.RingGeometry(SLOW_FIELD_RADIUS - 0.1, SLOW_FIELD_RADIUS + 0.1, 64);
      const ringMat = new THREE.MeshBasicMaterial({ color: 0x88aaff, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
      this.slowFieldHalo = new THREE.Mesh(ringGeo, ringMat);
      this.slowFieldHalo.rotation.x = -Math.PI / 2;
      this.slowFieldHalo.position.y = -1.05;
      this.engine.scene.add(this.slowFieldHalo);
    }
    if (this.slowFieldHalo) {
      const mat = this.slowFieldHalo.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.3 + 0.15 * Math.sin(elapsed * 2.5) + this.stacks.slow_field * 0.1;
    }

    // Advance enemies + leak detection. Use XZ plane (camera looks down y).
    // Slow Field upgrade is anti-cheat-friendly by design: it expands
    // the player's TAP_HIT_RADIUS inside SLOW_FIELD_RADIUS rather than
    // changing enemy speeds — server replay stays valid. (See
    // handleTap's tapRadius computation.)
    const stillActive: ActiveEnemy[] = [];
    let nearestDist = Infinity;
    for (const en of this.active) {
      const dt2 = elapsed - en.sched.spawn_t;
      const r = ARENA_RADIUS - en.sched.speed * dt2;
      if (r <= CORE_RADIUS) {
        this.engine.scene.remove(en.group);
        en.body.geometry.dispose();
        en.trail.geometry.dispose();
        this.leaks++;
        this.audio.thud();
        if (this.leaks >= LEAK_LIMIT) { this.endRun(elapsed); return; }
        continue;
      }
      en.group.position.set(Math.cos(en.sched.angle) * r, 0, Math.sin(en.sched.angle) * r);
      // Spin the body for life-feel; champions rotate faster.
      en.body.rotation.y = elapsed * (en.sched.champion ? 3 : 1.5);
      if (r < nearestDist) nearestDist = r;
      stillActive.push(en);
    }
    this.active = stillActive;

    // Approach rumble — distance from core to nearest enemy.
    if (nearestDist < APPROACH_GATE) {
      const intensity = 1 - (nearestDist - CORE_RADIUS) / (APPROACH_GATE - CORE_RADIUS);
      this.audio.setApproach(Math.max(0, Math.min(1, intensity)) * 0.9);
    } else {
      this.audio.setApproach(0);
    }

    // v6 — adaptive drone: max(ult charge ratio, leak danger ratio).
    // The bed thickens as kills accumulate toward the next ult AND as
    // leaks creep toward the limit; capped at 0.7. Per memory
    // feedback-sibling-games-shallow §"at least one escalation hook".
    const chargeRatio = Math.min(1, this.ultimateCharge / ULTIMATE_KILL_COST);
    const dangerRatio = Math.min(1, this.leaks / LEAK_LIMIT);
    const droneSignal = Math.max(chargeRatio, dangerRatio);
    try { this.audio.setDroneIntensity(0.15 + droneSignal * 0.55); } catch { /* off */ }

    // Core breathing — scale + emissive intensity track danger.
    const danger = this.leaks / LEAK_LIMIT;
    const breath = 1.0 + 0.06 * Math.sin(elapsed * 2.5) + danger * 0.18;
    this.core.scale.setScalar(breath);
    this.core.rotation.y = elapsed * 0.5;
    this.coreMat.emissiveIntensity = 1.2 + Math.sin(elapsed * 4) * 0.25 + danger * 0.8;
    (this.coreHalo.scale as THREE.Vector3).setScalar(breath * 1.05);

    // Tap-flash decay.
    if (this.flashOpacity > 0) {
      this.flashOpacity = Math.max(0, this.flashOpacity - dt * 3.5);
      (this.flashFx.material as THREE.MeshBasicMaterial).opacity = this.flashOpacity * 0.85;
      this.flashFx.scale.multiplyScalar(1 + dt * 1.8);
    }
    // Burst-ring decay.
    if (this.burstFx && this.burstFxOpacity > 0) {
      this.burstFxOpacity = Math.max(0, this.burstFxOpacity - dt * 2.0);
      (this.burstFx.material as THREE.MeshBasicMaterial).opacity = this.burstFxOpacity * 0.7;
      this.burstFx.scale.multiplyScalar(1 + dt * 2.5);
    }
    // Ultimate-ring decay.
    if (this.ultFx && this.ultFxOpacity > 0) {
      this.ultFxOpacity = Math.max(0, this.ultFxOpacity - dt * 1.4);
      (this.ultFx.material as THREE.MeshBasicMaterial).opacity = this.ultFxOpacity * 0.85;
      this.ultFx.scale.multiplyScalar(1 + dt * 1.8);
    }
    // v5 — engine boost pulse from ult: trauma-style cinema kick.
    if (this.ultBoost > 0.001) {
      this.engine.setBoost(this.ultBoost, dt);
      // Decay rate: ~1.4/s — the pulse lands in ~0.7s, longer than a
      // single ring flash so chromatic + god-rays have time to read.
      this.ultBoost = Math.max(0, this.ultBoost - dt * 1.4);
      if (this.ultBoost <= 0.001) {
        // Damp one last frame at 0 to relax post passes, then pin FOV
        // back to Bulwark's tight value (engine baseFov=70 would drift
        // the camera otherwise).
        this.engine.setBoost(0, dt);
        this.engine.camera.fov = this.BULWARK_FOV;
        this.engine.camera.updateProjectionMatrix();
      }
    }

    this.cb.onHud({
      durationS: elapsed,
      kills: this.kills,
      leaks: this.leaks,
      energy: this.energy,
      stacks: { ...this.stacks },
      shopOpen: this.shopOpen,
      shopTimeLeft: this.shopOpen ? Math.max(0, this.shopEndsAt - elapsed) : 0,
      ultimateCharge: this.ultimateCharge,
      ultimateReady: this.ultimateCharge >= ULTIMATE_KILL_COST,
    });
    if (elapsed >= MAX_DURATION_S) this.endRun(elapsed);
  }

  private endRun(elapsed: number) {
    this.gameOver = true;
    this.engine.stop();
    this.audio.setApproach(0);
    this.audio.setDroneIntensity(0);
    const durationS = Math.min(MAX_DURATION_S, elapsed);
    // Client-side estimate; server is authoritative for ranked score.
    const score = Math.max(
      0,
      this.kills * 100 + Math.round(durationS) * 10 - this.leaks * 100 +
        this.championKills * 200 + this.upgrades.length * 200,
    );
    let pb = 0;
    try { pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0; } catch { /* private mode */ }
    const isNewBest = score > pb;
    if (isNewBest) { try { localStorage.setItem(PB_KEY, String(score)); } catch { /* private mode */ } }
    this.cb.onGameOver({
      durationS,
      score,
      kills: this.kills,
      leaks: this.leaks,
      isNewBest,
      killClaims: this.killClaims,
      upgrades: this.upgrades,
      championKills: this.championKills,
      ultimates: this.ultimates,
      tankKills: this.tankKills,
      sprinterKills: this.sprinterKills,
      splitterKills: this.splitterKills,
    });
  }
}
